import React, {useCallback, useEffect, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {
  Backdrop,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  SvgIcon,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {AccountInfoContainer, RowEmailAdress, RowNameAndService, TypographyTitle} from './Style';
import {getSyncPreferenceSchema, getSyncPreferenceDefault} from './yupSchema';

import {iconMap, serviceNameMap} from '../AccountSettings';

import {RhfWatcher} from 'Components/RhfUtils';
import {ZmPrimaryBtn, ZmSecondaryBtn} from 'Components/ZmButtons';
import {ZmCheckbox} from 'Components/ZmCheckbox';
import {ZmDialog} from 'Components/ZmDialogs';
import {ZmRadio} from 'Components/ZmRadio';
import {updateSyncPreferences} from 'Store/IntegrationStore';
import {PROVIDER_TYPE} from 'Utils/consts';
import zoomPalette from 'zoomPalette';

/**
 * Component to manage Sync Preferences
 * @param {Object} props
 * @param {Account} props.account
 * @param {Account[]} props.accountList
 * @param {function} props.onClose
 * @param {function} props.onSubmitted
 * @return {ReactElement}
 */
export default function SyncPreference(props) {
  const {
    account,
    accountList,
    onClose,
    onSubmitted,
    open,
  } = props;

  const isEditing = account?.pushEnabled;

  const {t} = useTranslation();
  const dispatch = useDispatch();

  const formContext = useForm({
    defaultValues: getSyncPreferenceDefault(account),
    resolver: yupResolver(getSyncPreferenceSchema(t)),
  });

  useEffect(() => {
    if (open) {
      formContext.reset(getSyncPreferenceDefault(account));
    }
  }, [open, account, formContext]);

  const [changeLoading, setChangeLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    setChangeLoading(true);
    formContext.handleSubmit(
      async (formValues) => {
        try {
          await dispatch(updateSyncPreferences({
            accountEmail: formValues.selectedAccountEmail,
            accountService: formValues.selectedAccountService,
            pushCalendarId: formValues.pushCalendarId,
            webhooksEnabled: formValues.webhooksEnabled,
          })).unwrap();
          setChangeLoading(false);
          onSubmitted();
        } catch (e) {
          console.error(e);
          setChangeLoading(false);
        }
      },
      (fieldValues) => {
        console.error('validation err', fieldValues);
        setChangeLoading(false);
      })();
  }, [dispatch, formContext, onSubmitted]);

  const getCalendarOption = (field, accountCalendars) => accountCalendars.map((cal) => (
    <FormControlLabel
      key={cal.id}
      value={cal.id}
      onChange={field.onChange}
      sx={{marginLeft: '-9px'}}
      control={<ZmRadio id={cal.id} checked={field.value === cal.id} />}
      label={
        <FormLabel
          htmlFor={cal.id}
          sx={{
            color: zoomPalette.gray[90],
            display: 'inline-flex',
            alignItems: 'center',
            flexGrow: '1',
            marginLeft: 0,
            marginRight: 1,
            fontWeight: 500,
            fontSize: '.875rem',
          }}
        >
          <span aria-checked={field.value === cal.id}>
            {cal.name}
          </span>
        </FormLabel>
      }
    />
  ));

  return (
    <FormProvider {...formContext}>
      <Backdrop open={changeLoading}
        sx={{
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
      >
      </Backdrop>
      <ZmDialog open={open && !changeLoading}>
        <DialogTitle sx={{
          fontWeight: 'bold',
        }}>
          {t('integration.pushCalendarSettingsTitle')}
        </DialogTitle>
        <DialogContent>
          <TypographyTitle>{t('common.account')}</TypographyTitle>
          <AccountInfoContainer>
            <SvgIcon
              style={{width: '48px', height: '48px'}}
              component={iconMap[account?.service]}
              inheritViewBox
            ></SvgIcon>
            <div>
              <RowNameAndService>
                  ({serviceNameMap[account?.service]})
              </RowNameAndService>
              <RowEmailAdress>{account?.email}</RowEmailAdress>
            </div>
          </AccountInfoContainer>

          <Divider sx={{margin: '12px 0'}} />

          <Typography
            sx={{fontSize: '.875rem', fontWeight: '500', lineHeight: '24px'}}
          >
            {t('integration.calendarSelectionTitle')}
          </Typography>
          <Typography
            sx={{fontSize: '.875rem', fontWeight: '400', marginBottom: 1}}
          >
            {t('integration.calendarSelectionDesc')}
          </Typography>
          <RhfWatcher watchVals={['selectedAccountEmail']}>
            {(selectedAccountEmail) => {
              const selectedAccount = accountList.find((acc) => acc.email === selectedAccountEmail);
              const accountCalendars = (selectedAccount?.calendars || []).filter((cal) => cal.writeAccess);
              const [defaultCalendar, otherCalendars] = _.partition(accountCalendars, ['primary', true]);
              return (
                <Controller
                  name="pushCalendarId"
                  defaultValue={selectedAccount?.pushCalendarId}
                  render={({field, fieldState}) => {
                    const defaultCalendarOption = getCalendarOption(field, defaultCalendar);
                    const otherCalendarOptions = getCalendarOption(field, otherCalendars);
                    return (
                      <RadioGroup
                        // aria-labelledby="recurrence-end-options-label"
                        role={'radiogroup'}
                        sx={{maxHeight: 180, overflowY: 'auto', flexWrap: 'nowrap'}}
                        {...field}
                      >
                        {!!defaultCalendarOption?.length && !!otherCalendarOptions?.length && <Typography
                          sx={{
                            fontSize: '.875rem',
                            fontWeight: '400',
                            marginTop: 1,
                            color: zoomPalette.gray[60],
                          }}
                        >
                          {t('integration.defaultCalendar')}
                        </Typography>}
                        {defaultCalendarOption}
                        {!!defaultCalendarOption?.length && !!otherCalendarOptions?.length && <Typography
                          sx={{
                            fontSize: '.875rem',
                            fontWeight: '400',
                            marginTop: 1,
                            color: zoomPalette.gray[60],
                          }}
                        >
                          {t('integration.otherCalendars')}
                        </Typography>}
                        {otherCalendarOptions}
                      </RadioGroup>
                    );
                  }}
                />
              );
            }}
          </RhfWatcher>
          {/* apple doesn't support webhook */}
          {account?.service !== PROVIDER_TYPE.APPLE && <>
            <Divider sx={{margin: '12px 0'}} />

            <Typography
              sx={{fontSize: '.875rem', fontWeight: '500', lineHeight: '24px'}}
            >
              {t('integration.syncCancellationsTitle')}
            </Typography>
            <Controller
              name="webhooksEnabled"
              render={({field, fieldState}) =>
                <FormControlLabel
                  sx={{
                    'alignItems': 'start',
                    'marginLeft': '-8px',
                    '.MuiFormControlLabel-label': {
                      marginTop: '6px',
                      fontSize: '.875rem',
                    },
                  }}
                  control={
                    <ZmCheckbox
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    />}
                  label={t('integration.syncCancellationsDesc')}
                />
              }
            />
          </>}
        </DialogContent>
        <DialogActions>
          <>
            <ZmPrimaryBtn
              size="small"
              onClick={handleSubmit}
            >
              {isEditing ?
                t('common.saveChanges') :
                t('integration.switchPrimaryAccount')
              }
            </ZmPrimaryBtn>
            <ZmSecondaryBtn
              size="small"
              onClick={onClose}
            >
              {t('common.cancel')}
            </ZmSecondaryBtn>
          </>
        </DialogActions>
      </ZmDialog>
    </FormProvider>
  );
};
